import { useState, useEffect, useRef, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import storyDavid from "@/assets/story-david.png";
import storySarah from "@/assets/story-sarah.png";
import storyJames from "@/assets/story-james.png";

const stories = [
  {
    name: "David",
    image: storyDavid,
    story:
      "He remembered his mum's birthday from 6,000 miles away. The message he sent didn't sound like an app wrote it. It sounded like him.",
  },
  {
    name: "Sarah",
    image: storySarah,
    story:
      "She hadn't reached out to her closest friend in three months. Circle noticed. One nudge later, a friendship that was quietly fading came back to life.",
  },
  {
    name: "James",
    image: storyJames,
    story:
      "He never knows what to get people. Circle remembered that his manager loves golf and had a work anniversary coming up. The gift felt personal because it was.",
  },
];

const StoryCards = () => {
  const prefersReducedMotion = useReducedMotion();
  const [activeIndex, setActiveIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isPaused = useRef(false);
  const timerRef = useRef<ReturnType<typeof setInterval>>();

  const scrollToIndex = useCallback((idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / stories.length;
    el.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
  }, []);

  // Auto-advance every 5s on mobile, pause on touch hold
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || prefersReducedMotion) return;

    const startTimer = () => {
      clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        if (isPaused.current) return;
        setActiveIndex((prev) => {
          const next = (prev + 1) % stories.length;
          scrollToIndex(next);
          return next;
        });
      }, 5000);
    };

    const handleTouchStart = () => { isPaused.current = true; };
    const handleTouchEnd = () => {
      isPaused.current = false;
      startTimer(); // reset timer after release
    };

    el.addEventListener("touchstart", handleTouchStart, { passive: true });
    el.addEventListener("touchend", handleTouchEnd, { passive: true });

    startTimer();

    return () => {
      clearInterval(timerRef.current);
      el.removeEventListener("touchstart", handleTouchStart);
      el.removeEventListener("touchend", handleTouchEnd);
    };
  }, [prefersReducedMotion, scrollToIndex]);

  const staggerItem = (delay: number) =>
    prefersReducedMotion
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true },
          transition: { duration: 0.5, ease: "easeOut" as const, delay },
        };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const scrollLeft = el.scrollLeft;
    const cardWidth = el.scrollWidth / stories.length;
    const idx = Math.round(scrollLeft / cardWidth);
    setActiveIndex(idx);
  };

  return (
    <section className="bg-background px-6 py-20 md:py-28">
      <div className="mx-auto max-w-4xl text-center">
        <motion.span
          className="mb-4 inline-block text-xs font-semibold uppercase tracking-[0.2em] text-accent"
          {...staggerItem(0)}
        >
          Real Moments
        </motion.span>
        <motion.h2
          className="mb-16 font-serif text-3xl font-semibold text-foreground md:text-4xl"
          {...staggerItem(0.1)}
        >
          What showing up actually looks like
        </motion.h2>

        {/* Desktop grid */}
        <div className="hidden md:grid md:grid-cols-3 md:gap-6">
          {stories.map((s, i) => (
            <motion.div
              key={s.name}
              className="flex flex-col rounded-2xl border-l-[3px] bg-card p-8 text-left shadow-sm"
              style={{ borderLeftColor: "#C9A84C" }}
              {...staggerItem(i * 0.15)}
            >
              <img
                src={s.image}
                alt={s.name}
                className="mb-6 h-[120px] w-[120px] rounded-full object-cover object-top"
              />
              <p className="mb-4 flex-1 font-serif text-lg italic leading-relaxed text-foreground md:text-xl">
                "{s.story}"
              </p>
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-accent">
                {s.name}
              </span>
            </motion.div>
          ))}
        </div>

        {/* Mobile horizontal scroll */}
        <div className="md:hidden">
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide"
            style={{
              scrollSnapType: "x mandatory",
              WebkitOverflowScrolling: "touch",
            }}
            onScroll={handleScroll}
          >
            {stories.map((s, i) => (
              <motion.div
                key={s.name}
                className="flex w-[85vw] flex-shrink-0 flex-col items-center rounded-2xl bg-card p-6 text-center shadow-sm"
                style={{ scrollSnapAlign: "start" }}
                {...staggerItem(i * 0.15)}
              >
                <img
                  src={s.image}
                  alt={s.name}
                  className="mb-5 h-[120px] w-[120px] rounded-full object-cover object-top"
                />
                <p className="mb-4 flex-1 font-serif text-lg italic leading-relaxed text-foreground">
                  "{s.story}"
                </p>
                <span className="text-xs font-semibold uppercase tracking-[0.15em] text-accent">
                  {s.name}
                </span>
              </motion.div>
            ))}
          </div>

          {/* Scroll indicator dots */}
          <div className="mt-4 flex justify-center gap-2">
            {stories.map((_, i) => (
              <div
                key={i}
                className="h-2 w-2 rounded-full transition-colors"
                style={{
                  backgroundColor:
                    i === activeIndex ? "#C9A84C" : "hsl(var(--muted))",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoryCards;
